import clsx from "clsx";
import { debounce, isEmpty, noop } from "lodash";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "../Button";
import { FILTER_DEBOUNCE_TIME } from "./constants";
import "./Table.scss";

const totalSizeFormatter = (total) =>
<FormattedMessage id="UIC.TABLE.TOTAL_SIZE" values={{ total }} />;


const selectedSizeFormatter = (total) =>
<FormattedMessage id="UIC.TABLE.TOTAL_SELECTED" values={{ total }} />;


const propTypes = {
  /**
   * Total number of rows in the table
   */
  rowCount: PropTypes.number,
  /**
   * Number of selected rows on the table
   */
  selectedCount: PropTypes.number, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to format total size message and pass following arguments: totalSize
   */
  formatTotalSizeMessage: PropTypes.func,
  /**
   * Callback to format total size message and pass following arguments: selectedSize
   */
  formatSelectedSizeMessage: PropTypes.func,
  /**
   * Callback to manipulate selected rows
   */
  onSelectAll: PropTypes.func,
  /**
   * function to render components in the table header and pass the following arguments: selectedCount
   */
  headerActionsRenderer: PropTypes.func,
  /**
   * If true, indicates that the table is loading
   */
  loading: PropTypes.bool,
  /**
   * Array of searchable columns
   */
  searchable: PropTypes.arrayOf(PropTypes.string),
  /**
   * Global search value
   */
  search: PropTypes.string,
  /**
   * Callback to set the global search value
   */
  setSearch: PropTypes.func.isRequired,
  /**
   * Callback called with the search value when the global 'Search' input on the top of the table changes
   */
  onGlobalSearchChange: PropTypes.func,
  /**
   * Called when 'Select All Pages' button is clicked, it does NOT pass any arguments.
   * 'Select All Pages' button will only appear if this callback is passed.
   */
  onSelectAllPages: PropTypes.func,
  /**
   * Indicates that the 'select all pages' button is loading
   */
  loadingSelectAllPages: PropTypes.bool,
  /**
   * Indicates the refresh click functionality
   */
  onRefreshClick: PropTypes.func,
  /**
   * should refresh button show
   */
  showRefresh: PropTypes.bool
};


















function TableHeader(props) {
  const {
    formatTotalSizeMessage = totalSizeFormatter,
    formatSelectedSizeMessage = selectedSizeFormatter,
    rowCount = undefined,
    selectedCount = 0,
    onSelectAll = undefined,
    headerActionsRenderer = () => null,
    loading = false,
    searchable = [],
    search = "",
    setSearch,
    onGlobalSearchChange = noop,
    onSelectAllPages = undefined,
    loadingSelectAllPages = false,
    onRefreshClick = () => {},
    showRefresh = false
  } = props ?? {};

  const intl = useIntl();

  const debouncedOnSearch = useMemo(
    () => debounce(onGlobalSearchChange, FILTER_DEBOUNCE_TIME),
    [onGlobalSearchChange]
  );

  const actions = headerActionsRenderer(selectedCount);

  return (
    <div className="mb-3 d-flex justify-content-between">
      {/* left side */}
      <div
        className={clsx("Uic-Table-headerLeft", "d-flex align-items-center")}>

        <span className={clsx({ "d-none": selectedCount })}>
          {formatTotalSizeMessage(rowCount)}
        </span>
        {showRefresh &&
        <Button
          variant="light"
          className={`fa fa-rotate ms-3 d-inline-flex align-items-center justify-content-center
             ${clsx(
            {
              "d-none": selectedCount,
              "fa-spin btnNoBg": loading
            },
            "Uic-Table-fontColorEngage"
          )}`}
          onClick={onRefreshClick}
          disabled={loading}
          title={intl.formatMessage({ id: "UIC.GENERAL.REFRESH" })} />

        }
        <span className={clsx({ "d-none": !selectedCount })}>
          {formatSelectedSizeMessage(selectedCount)}
        </span>
        <Button
          variant="link"
          className={clsx({ invisible: !selectedCount }, "ms-3")} // making it invisible to maintain header height
          onClick={() => onSelectAll(false)}
          disabled={loading}>

          <FormattedMessage id="UIC.TABLE.CLEAR_SELECTION" />
        </Button>
        <Button
          variant="link"
          className={clsx(
            { "d-none": !selectedCount || !onSelectAllPages },
            "ms-3"
          )}
          onClick={() => onSelectAllPages()}
          disabled={loading || loadingSelectAllPages}>

          <FormattedMessage id="UIC.TABLE.SELECT_ALL_PAGES" />
        </Button>
      </div>
      {/* right side */}
      <div className="d-flex align-items-center">
        {selectedCount && actions ?
        actions :

        <>
            {actions}
            <Form.Control
            className={clsx("ms-3", "Uic-Table-input", {
              "d-none": isEmpty(searchable)
            })}
            placeholder={intl.formatMessage({ id: "UIC.TABLE.SEARCH" })}
            value={search}
            onChange={(event) => {
              const { value } = event.target;
              setSearch(value);
              debouncedOnSearch(value);
            }} />

          </>
        }
      </div>
    </div>);

}

TableHeader.propTypes = propTypes;


export default TableHeader;
